import {ExclamationTriangleIcon} from '@heroicons/react/20/solid'
import React, {useState} from 'react'
import LoadingIndicator from '../../LoadingIndicator'
import defaultUrl from '../assets/default_image.png'
import DeleteIcon from '../assets/DeleteIcon'
import UploadIcon from '../assets/UploadIcon'
import DeleteConfirmationPopup from './DeleteConfirmationPopup'
import ImageCropper from './ImageCropper'

// Configurable size system - can be tweaked in one place
const IMAGE_UPLOAD_SIZES = {
  small: {width: 75, height: 75}, // Current lesson slot size
  sponsor: {width: 192, height: 128}, // For sponsor dialogs - much larger
  list: {width: 96, height: 96} // For sponsor list view - much larger
} as const

type ImageUploadSize = keyof typeof IMAGE_UPLOAD_SIZES

interface Props {
  uploadKey: string
  uri: string | null
  onUpload: (key: string, image: File) => void
  onDelete: (key: string) => void
  size?: ImageUploadSize
}

const ImageUpload = ({
  uploadKey,
  uri,
  onUpload,
  onDelete,
  size = 'small'
}: Props) => {
  const widthClass =
    size === 'sponsor' ? 'w-48' : size === 'list' ? 'w-32' : 'w-[75px]'
  const heightClass =
    size === 'sponsor' ? 'h-32' : size === 'list' ? 'h-24' : ''
  const [showDeleteConfirmationPopup, setShowDeleteConfirmationPopup] =
    useState(false)
  const [showImageCropper, setShowImageCropper] = useState(false)
  const [imageSrcInvalid, setImageSrcInvalid] = useState(false)

  const uploadCroppedImage = (croppedFile: File) => {
    setShowImageCropper(false)
    if (croppedFile !== null) {
      onUpload(uploadKey, croppedFile)
    }
  }
  const [imageLoaded, setImageLoaded] = useState(false)

  return (
    <div className={`relative flex ${widthClass} justify-start`}>
      {uri == null ? (
        <div
          className="cursor-pointer focus:outline-none"
          onClick={() => setShowImageCropper(true)}
        >
          <img
            className={`${widthClass} ${heightClass} rounded-lg object-contain opacity-20`}
            src={defaultUrl}
            alt={uploadKey}
          />
          <div className="absolute bottom-0 left-0 z-10">
            <UploadIcon />
          </div>
        </div>
      ) : (
        <>
          {!imageLoaded && (
            <div className="flex size-full items-center justify-center">
              <LoadingIndicator />
            </div>
          )}
          {!imageSrcInvalid && (
            <img
              className={`${widthClass} aspect-video rounded-lg border border-[#ececea] object-contain ${!imageLoaded ? 'hidden' : ''}`}
              src={uri}
              alt={uploadKey}
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageLoaded(true)
                setImageSrcInvalid(true)
              }}
            />
          )}
          {imageSrcInvalid && (
            <div
              title={` ${uploadKey} image failed to load`}
              className={`relative flex ${widthClass} ${heightClass} justify-center`}
            >
              <img
                className={`${widthClass} ${heightClass} rounded-lg object-cover opacity-20`}
                src={defaultUrl}
                onLoad={() => setImageLoaded(true)}
                alt={uploadKey}
              />
              <ExclamationTriangleIcon
                strokeWidth={1.2}
                className="absolute m-auto flex size-8 cursor-pointer self-center fill-transparent stroke-orange-tertiary-20"
              />
              <div
                className="absolute bottom-0 left-0 z-10 cursor-pointer"
                onClick={() => setShowImageCropper(true)}
              >
                <UploadIcon />
              </div>
            </div>
          )}
          {imageLoaded && !imageSrcInvalid && (
            <div
              className="absolute -right-1 -top-1 z-10"
              onClick={() => setShowDeleteConfirmationPopup(true)}
            >
              <DeleteIcon />
            </div>
          )}
        </>
      )}

      <DeleteConfirmationPopup
        open={showDeleteConfirmationPopup}
        onAccept={() => {
          onDelete(uploadKey)
          setShowDeleteConfirmationPopup(false)
        }}
        onCancel={() => setShowDeleteConfirmationPopup(false)}
      />

      <ImageCropper
        open={showImageCropper}
        onCrop={cropped => uploadCroppedImage(cropped)}
        onCancel={() => setShowImageCropper(false)}
      />
    </div>
  )
}

export default ImageUpload
